import { useTranslation } from 'react-i18next'

function HexMark({ size = 26 }) {
  return (
    <svg
      className="tg-mark"
      viewBox="0 0 32 32" width={size} height={size} fill="none"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="hexTop">
          <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="32" height="18.6" fill="currentColor" opacity="0.16" clipPath="url(#hexTop)" />
      <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <line x1="6" y1="18.6" x2="26" y2="18.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function userInitials(user) {
  if (!user) return '?'
  const n = user.name || user.email || ''
  const parts = n.split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return n.slice(0, 2).toUpperCase()
}

export default function AppShell({
  brandName,
  truckCount,
  tabs,
  activeTab,
  onTabChange,
  user,
  alertCount,
  tamperCount,
  onAlertClick,
  onSignOut,
  secondsAgo,
  live,
  mobile,
}) {
  const { t } = useTranslation()
  const timeLabel = secondsAgo === undefined
    ? null
    : secondsAgo < 60
      ? secondsAgo + t('dashboard.secondsAgo')
      : Math.floor(secondsAgo / 60) + t('dashboard.minutesAgo')

  return (
    <header className="app-top">
      {/* Left: brand mark + "Tankguard" + divider + org name + truck count */}
      <div className="app-brand">
        <HexMark size={26} />
        <span className="wm">Tankguard</span>
      </div>
      {!mobile && brandName && (
        <div className="app-org">
          <span className="org-name">{brandName}</span>
          {truckCount !== undefined && (
            <span className="org-cnt">{truckCount} trucks</span>
          )}
        </div>
      )}

      {/* Center: tab nav */}
      <nav className="app-tabs" style={{ display: 'flex', alignItems: 'center', gap: 2, margin: '0 auto', height: '100%' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => onTabChange(t.key)}
            className={`tg-tab${activeTab === t.key ? ' is-active' : ''}`}
            style={t.key === 'admin' ? { color: activeTab === 'admin' ? '#a78bfa' : '#7c3aed' } : undefined}
          >
            {mobile ? t.abbr : t.label}
          </button>
        ))}
      </nav>

      {/* Right: alert pill + updated + avatar */}
      <div className="top-right">
        {tamperCount > 0 && (
          <span
            className="alert-pill"
            onClick={onAlertClick}
            title="Physical sensor tampering — critical"
            style={{ background: 'rgba(234, 88, 12, 0.12)', color: '#9a3412', border: '1px solid rgba(234, 88, 12, 0.4)' }}
          >
            {/* Wrench glyph */}
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
              <path d="M14.7 6.3a4 4 0 1 0 5 5l-1.4 1.4 4 4-2.8 2.8-4-4-1.4 1.4a4 4 0 0 1-5-5l2.5 2.5 2.6-2.6L12 4.7 14.7 6.3z" />
            </svg>
            {tamperCount} tamper{tamperCount !== 1 ? 's' : ''}
          </span>
        )}
        {alertCount > 0 && (
          <span className="alert-pill" onClick={onAlertClick}>
            <span className="dot" />
            {alertCount} alert{alertCount !== 1 ? 's' : ''}
          </span>
        )}
        {/* Connection state: a live SSE stream replaces the poll-age label.
            Green is the semantic "online" token, not brand teal. */}
        {live && !mobile ? (
          <span className="updated" title={t('dashboard.liveHint')}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tg-success-500)', display: 'inline-block', marginRight: 6, verticalAlign: 'middle' }} />
            <b>{t('dashboard.live')}</b>
          </span>
        ) : timeLabel && !mobile ? (
          <span className="updated">{t('dashboard.updatedAgo')} <b>{timeLabel}</b></span>
        ) : null}
        <div className="avatar" title={user?.email}>
          {userInitials(user)}
        </div>
        {onSignOut && !mobile && (
          <button
            onClick={onSignOut}
            className="tg-btn tg-btn--ghost tg-btn--sm"
            style={{ color: 'var(--tg-graphite-400)' }}
          >
            {t('dashboard.signOut')}
          </button>
        )}
        {onSignOut && mobile && (
          <button
            onClick={onSignOut}
            className="tg-btn tg-btn--ghost tg-btn--sm"
            style={{ color: 'var(--tg-graphite-400)', padding: '0 6px' }}
            aria-label="Sign out"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 17, height: 17 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" />
            </svg>
          </button>
        )}
      </div>
    </header>
  )
}
